import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Menu, MenuItem } from "@mui/material";
import { useSearchParams } from "react-router-dom";

/**
 * Absolute URL for sharing (origin + path + query).
 * @param {string} pathWithQuery e.g. "/catalog-items?q=abc&t=0"
 */
export function absoluteAppUrl(pathWithQuery) {
    const path = String(pathWithQuery || "");
    const normalized = path.startsWith("/") ? path : `/${path}`;
    if (typeof window === "undefined" || !window.location?.origin) {
        return normalized;
    }
    return `${window.location.origin}${normalized}`;
}

/** Build absolute URL for pathname + query object (skips empty values). */
export function buildPathShareUrl(pathname, query = {}) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query || {})) {
        if (value == null || value === "") continue;
        params.set(key, String(value));
    }
    const path = String(pathname || "").startsWith("/")
        ? String(pathname)
        : `/${pathname || ""}`;
    const qs = params.toString();
    return absoluteAppUrl(`${path}${qs ? `?${qs}` : ""}`);
}

/**
 * Debounced search box ↔ `?q=` URL sync (preserves other params).
 * Use instead of local-only search state when share links should filter the grid.
 */
export function useSyncedUrlSearchQuery() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchInput, setSearchInput] = useState(
        () => searchParams.get("q") || ""
    );
    const [searchQ, setSearchQ] = useState(
        () => (searchParams.get("q") || "").trim()
    );

    useEffect(() => {
        const timer = setTimeout(() => {
            const nextQ = searchInput.trim();
            setSearchQ(nextQ);
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    if (nextQ) next.set("q", nextQ);
                    else next.delete("q");
                    if (next.toString() === prev.toString()) return prev;
                    return next;
                },
                { replace: true }
            );
        }, 300);
        return () => clearTimeout(timer);
    }, [searchInput, setSearchParams]);

    return {
        searchInput,
        setSearchInput,
        searchQ,
        setSearchQ,
        searchParams,
        setSearchParams,
    };
}

export async function copyTextToClipboard(text) {
    const value = String(text || "");
    if (!value) throw new Error("Nothing to copy.");
    if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        return;
    }
    const el = document.createElement("textarea");
    el.value = value;
    el.setAttribute("readonly", "");
    el.style.position = "absolute";
    el.style.left = "-9999px";
    document.body.appendChild(el);
    el.select();
    document.execCommand("copy");
    document.body.removeChild(el);
}

/** Build share URL for a catalog item row (filters list by itemId). */
export function buildCatalogItemShareUrl(row, { tab } = {}) {
    return buildPathShareUrl("/catalog-items", {
        q: String(row?.itemId || "").trim(),
        t: tab != null && String(tab) !== "" ? String(tab) : "",
    });
}

/** Build share URL for a product row (filters list by SKU). */
export function buildProductShareUrl(row, { tab } = {}) {
    return buildPathShareUrl("/products", {
        q: String(row?.listingField || row?.internalSkuId || "").trim(),
        t: tab != null && String(tab) !== "" ? String(tab) : "",
    });
}

/** Build share URL for an order row (filters list by order number). */
export function buildOrderShareUrl(row, { tab } = {}) {
    return buildPathShareUrl("/orders", {
        order: String(row?.orderNumber || "").trim(),
        t: tab != null && String(tab) !== "" ? String(tab) : "",
    });
}

/** Build share URL for an invoice row (opens detail panel). */
export function buildInvoiceShareUrl(row, { tab } = {}) {
    const allowed = new Set(["incoming", "received", "buckets", "all"]);
    return buildPathShareUrl("/invoices", {
        id: row?.id != null ? String(row.id) : "",
        n: String(row?.invoiceNumber || "").trim(),
        tab: allowed.has(tab) ? tab : "",
    });
}

export function buildReturnsShareUrl(row) {
    const q =
        String(row?.rma || "").trim() ||
        String(row?.orderNumber || "").trim() ||
        String(row?.returnId || "").trim() ||
        String(row?.skuName || "").trim();
    return buildPathShareUrl("/returns", { q });
}

export function buildFulfillmentShareUrl(row) {
    const q =
        String(row?.itemId || "").trim() ||
        String(row?.orderNumber || row?.customerOrderId || "").trim() ||
        String(row?.sku || "").trim();
    return buildPathShareUrl("/fulfillment", { q });
}

export function buildShipmentShareUrl(row, { tab } = {}) {
    const q =
        String(row?.customerOrderNumber || "").trim() ||
        String(row?.itemId || "").trim() ||
        String(row?.trackingNumber || "").trim();
    return buildPathShareUrl("/order-shipping", {
        t: tab != null && String(tab) !== "" ? String(tab) : "0",
        q,
    });
}

export function buildInventoryLevelsShareUrl(row) {
    const q =
        String(row?.internalSkuId || "").trim() ||
        String(row?.listingField || "").trim() ||
        String(row?.itemName || "").trim();
    return buildPathShareUrl("/products", { t: "2", q });
}

export function buildShelfConfigShareUrl(row) {
    const q = String(row?.shelfName || row?.name || "").trim();
    return buildPathShareUrl("/shelf-configs", { q });
}

export function buildPrintLabelShareUrl(row) {
    const q = String(row?.parserItemId || row?.itemId || "").trim();
    return buildPathShareUrl("/catalog-items", { t: "1", q });
}

/** Dimension / catalog dimension tables — share current path + search. */
export function buildDimensionShareUrl(row, { pathname, tab } = {}) {
    const q =
        String(row?.label || "").trim() ||
        String(row?.internalSkuId || "").trim() ||
        String(row?.name || "").trim() ||
        String(row?.upc || "").trim();
    const path = String(pathname || "/products").split("?")[0] || "/products";
    return buildPathShareUrl(path, {
        ...(path.startsWith("/products") ? { t: tab != null ? String(tab) : "1" } : {}),
        ...(tab != null && !path.startsWith("/products") ? { t: String(tab) } : {}),
        q,
    });
}

export function buildUserShareUrl(row) {
    const q = String(row?.email || "").trim();
    return buildPathShareUrl("/manage-account", { page: "1", q });
}

/** Standard Copy share URL menu action. */
export function copyShareUrlAction({ buildUrl, disabled, onCopied }) {
    return {
        id: "copyShareUrl",
        label: "Copy share URL",
        disabled,
        onClick: async (row) => {
            await copyTextToClipboard(buildUrl(row));
            onCopied?.();
        },
    };
}

/**
 * Hook for DataGrid row right-click menus.
 * Use with slotProps.row.onContextMenu via rowContextMenuSlotProps().
 */
export function useGridRowContextMenu() {
    const [menu, setMenu] = useState(null);

    const close = useCallback(() => setMenu(null), []);

    const onRowContextMenu = useCallback((event, row) => {
        event.preventDefault();
        event.stopPropagation();
        if (!row) return;
        setMenu({
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
            row,
        });
    }, []);

    const rowContextMenuSlotProps = useCallback(
        (rows, baseSlotProps = {}) => {
            const byId = new Map(
                (rows || []).map((row) => [String(row.id), row])
            );
            return {
                ...baseSlotProps,
                row: {
                    ...(baseSlotProps.row || {}),
                    onContextMenu: (event) => {
                        const id = event.currentTarget?.getAttribute("data-id");
                        const row = id != null ? byId.get(String(id)) : null;
                        onRowContextMenu(event, row);
                    },
                },
            };
        },
        [onRowContextMenu]
    );

    return { menu, close, onRowContextMenu, rowContextMenuSlotProps };
}

/**
 * Extensible row context menu.
 * @param {{ menu: { mouseX: number, mouseY: number, row: object } | null, onClose: () => void, actions: Array<{ id: string, label: string, onClick: (row) => void | Promise<void>, disabled?: (row) => boolean }> }} props
 */
export default function GridRowContextMenu({ menu, onClose, actions = [] }) {
    const open = Boolean(menu);
    const row = menu?.row;

    const items = useMemo(
        () => (Array.isArray(actions) ? actions.filter(Boolean) : []),
        [actions]
    );

    return (
        <Menu
            open={open}
            onClose={onClose}
            anchorReference="anchorPosition"
            anchorPosition={
                open
                    ? { top: menu.mouseY, left: menu.mouseX }
                    : undefined
            }
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: "10px",
                        minWidth: 180,
                        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
                    },
                },
            }}
        >
            {items.map((action) => {
                const disabled =
                    typeof action.disabled === "function"
                        ? Boolean(action.disabled(row))
                        : Boolean(action.disabled);
                return (
                    <MenuItem
                        key={action.id}
                        disabled={disabled}
                        onClick={async () => {
                            try {
                                await action.onClick?.(row);
                            } finally {
                                onClose?.();
                            }
                        }}
                        sx={{ fontSize: 14, fontWeight: 600 }}
                    >
                        {action.label}
                    </MenuItem>
                );
            })}
        </Menu>
    );
}
